import React, { useState, useEffect, useRef } from 'react';
import { sendVerificationCode, verifyEmailCode } from '../firebase/emailVerification';
import { useT } from '../i18n';
import { isPhoneAlias } from '../constants/phone';
import '../pages/auth.css';

const RESEND_COOLDOWN_S = 60;

/**
 * Drop-in email OTP step: a "Send Code" button that reveals a 6-digit input
 * and a "Verify" button once a code has been sent. Place it wherever a flow
 * needs to confirm an email is real (e.g. before actually creating the
 * account), passing the email the user already typed and a callback for once
 * it's confirmed.
 *
 * Resets its own sent/verified state if `email` changes after a code was
 * already sent/verified — a code issued for one address must not silently
 * carry over to a different one if the user edits the field afterward. The
 * PARENT should mirror that by clearing its own "verified" flag whenever the
 * email field changes (see RegisterForm's email onChange).
 *
 * <EmailCodeVerify email={email} onVerified={() => ...} />
 */
export default function EmailCodeVerify({ email, onVerified, purpose = 'register' }) {
  const { t } = useT();
  const [code, setCode]         = useState('');
  const [sending, setSending]   = useState(false);
  const [checking, setChecking] = useState(false);
  const [sent, setSent]         = useState(false);
  const [verified, setVerified] = useState(false);
  const [error, setError]       = useState('');
  const [cooldown, setCooldown] = useState(0);
  const intervalRef = useRef(null);
  const lastEmailRef = useRef(email);

  useEffect(() => () => clearInterval(intervalRef.current), []);

  useEffect(() => {
    if (lastEmailRef.current !== email) {
      lastEmailRef.current = email;
      clearInterval(intervalRef.current);
      setSent(false);
      setVerified(false);
      setCode('');
      setError('');
      setCooldown(0);
    }
  }, [email]);

  function startCooldown() {
    setCooldown(RESEND_COOLDOWN_S);
    clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      setCooldown((c) => {
        if (c <= 1) { clearInterval(intervalRef.current); return 0; }
        return c - 1;
      });
    }, 1000);
  }

  async function handleSendCode() {
    setError('');
    if (!/\S+@\S+\.\S+/.test(email || '')) { setError(t('auth_enter_email_first')); return; }
    // The internal identifier phone accounts carry — never a real mailbox, and
    // accepting it here would be the first step of taking over the account that
    // owns that number. Refused server-side too; this is only for the message.
    if (isPhoneAlias(email)) { setError(t('auth_err_email_reserved')); return; }
    setSending(true);
    try {
      await sendVerificationCode(email, purpose);
      setSent(true);
      startCooldown();
    } catch (err) {
      // Prefer a translated message keyed off the error CODE — the Cloud
      // Function's own message is English-only, and this form runs in three
      // locales. Falls back to the server text for anything unmapped.
      // A Google-registered address gets its own message — "already exists" on
      // its own is a dead end for someone who has no password to sign in with.
      const taken = err?.details?.provider === 'google.com'
        ? t('auth_err_email_google')
        : t('auth_err_email_taken');
      const byCode = {
        'functions/already-exists': taken,
        'functions/not-found':      t('auth_err_no_account'),
      }[err?.code];
      setError(byCode || err.message || t('auth_err_email_inv'));
    } finally {
      setSending(false);
    }
  }

  async function handleVerify(e) {
    e.preventDefault();
    setError('');
    if (!/^\d{6}$/.test(code)) { setError(t('auth_enter_code')); return; }
    setChecking(true);
    try {
      await verifyEmailCode(email, code);
      setVerified(true);
      onVerified && onVerified();
    } catch (err) {
      setError(err.message || t('auth_enter_code'));
    } finally {
      setChecking(false);
    }
  }

  if (verified) {
    return (
      <div className="af-toast af-toast--success" role="status">
        <span className="af-toast__icon">✓</span>
        {t('auth_email_verified')}
      </div>
    );
  }

  return (
    <div className="af-field">
      {!sent ? (
        <button type="button" className="af-btn" onClick={handleSendCode} disabled={sending}>
          {sending && <span className="af-spinner" />}
          {sending ? t('auth_sending_code') : t('auth_send_code')}
        </button>
      ) : (
        <form onSubmit={handleVerify}>
          <label className="af-label" htmlFor="otp-code">{t('auth_code_label')}</label>
          <div className="af-input-wrap">
            <input
              id="otp-code"
              className="af-input af-input--no-icon"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              placeholder="123456"
              value={code}
              onChange={(e) => { setCode(e.target.value.replace(/\D/g, '').slice(0, 6)); setError(''); }}
              disabled={checking}
              autoFocus
            />
          </div>
          {/* WHERE THE MESSAGE ACTUALLY WENT. A verification code from a domain
              the mailbox has never seen before is a routine catch for spam
              filters, and someone staring at an empty inbox concludes the send
              failed and presses Resend — which sends a second mail down the
              same path and makes the reputation problem slightly worse.

              Under the input rather than beside the Resend button: it should be
              read while WAITING, not only after deciding nothing arrived. */}
          <p className="af-hint">{t('auth_code_spam_hint')}</p>
          <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
            <button type="submit" className="af-btn" disabled={checking}>
              {checking && <span className="af-spinner" />}
              {checking ? t('auth_verifying') : t('auth_verify_btn')}
            </button>
            <button
              type="button"
              className="af-google-btn"
              onClick={handleSendCode}
              disabled={sending || cooldown > 0}
            >
              {cooldown > 0 ? `${t('auth_resend_in')} ${cooldown}s` : t('auth_resend_code')}
            </button>
          </div>
        </form>
      )}
      {error && <p className="af-field-error">{error}</p>}
    </div>
  );
}
